import { CSSProperties, useCallback, useMemo, useRef } from 'react';
import {
  ESVirtualizedTable,
  ESVirtualizedTableProps,
} from './ESVirtualizedTable';
import { useDrop } from 'react-dnd';
import { DndRowsOptions, DroppableItem } from './DndRowsOptions';
import {
  ESTableDraggableRow,
  ESTableDraggableRowProps,
} from './ESTableDraggableRow';
import { Row } from '@tanstack/react-table';
import { useScrollOnDrag } from '../useScrollOnDrag';
import { ItemProps } from 'react-virtuoso';
import { alpha, useTheme } from '@mui/material';

interface ESDnDVirtualizedTableProps<T>
  extends Omit<ESVirtualizedTableProps<T>, 'scrollerRef' | 'renderRow'> {
  dndRowsOptions: DndRowsOptions<T>;
  applyRowStyles?: (row: Row<T>) => ESTableDraggableRowProps<T>['rowStyles'];
  onRowHover?: (row: Row<T> | null) => void;
}

export const ESDnDVirtualizedTable = <T,>({
  instance,
  rows,
  rowHeight,
  dndRowsOptions,
  testId,
  applyRowStyles,
  onRowHover,
}: ESDnDVirtualizedTableProps<T>) => {
  const { palette } = useTheme();
  const [{ draggedItemId }, dropRef] = useDrop(
    {
      accept: 'row',
      collect: (monitor) => ({
        draggedItemId: monitor.getItem()?.id,
      }),
      drop: (item: DroppableItem<T>, monitor: { didDrop: () => boolean }) => {
        const didDrop = monitor.didDrop();
        if (didDrop) {
          return;
        }

        requestAnimationFrame(() => {
          // Drop handler on the table used when there is no rows in the table that can listen to the drop event
          dndRowsOptions.onDrop?.(
            item.selectedRowIds.length > 0
              ? item.selectedRowIds
              : [dndRowsOptions.dragRowsSelectId?.(item.row) || ''],
            ''
          );
        });
      },
    },
    [dndRowsOptions.onDrop]
  );

  const tableContainerRef = useRef<HTMLElement | null>(null);
  useScrollOnDrag({
    scrollContainer: tableContainerRef.current,
  });

  const isLocalDrag = useMemo(() => {
    return Boolean(rows.find((row) => row.id === draggedItemId));
  }, [draggedItemId, rows]);

  const rowOptions = useMemo(
    () => ({
      ...dndRowsOptions,
      onRowClick: (event: React.MouseEvent, row: Row<T>) => {
        dndRowsOptions.onRowClick?.(
          event,
          row,
          instance.getFilteredRowModel().rows.map((row) => row.original)
        );
      },
    }),
    [dndRowsOptions, instance]
  );

  const renderRow = useCallback(
    (props: ItemProps<T>) => {
      const index = props['data-index'];
      const row = rows[index];

      const rowStyles = {
        ...(applyRowStyles?.(row) ?? {}),
        ...(dndRowsOptions.canDrag && !dndRowsOptions.canDrag(row)
          ? {
              position: 'relative',
              cursor: 'not-allowed',
              // transform and clipPath needed in order for "disabled" effect to work in Safari
              // because Safari doesn't support position on <tr> element
              // Sources: https://github.com/w3c/csswg-drafts/issues/1899 and https://mtsknn.fi/blog/relative-tr-in-safari/
              transform: 'translate(0)',
              clipPath: 'inset(0)',
              '&::after': {
                content: '""',
                display: 'block',
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                backgroundColor: alpha(palette.background.paper, 0.5),
                zIndex: 1,
              },
            }
          : {}),
      } satisfies CSSProperties;

      return (
        <ESTableDraggableRow
          key={row.id}
          data-testid={`${testId}TableRow${row.index}`}
          row={row}
          rowStyles={rowStyles}
          isLocalDrag={isLocalDrag}
          dndRowsOptions={rowOptions}
          onMouseEnter={() => {
            onRowHover?.(row);
          }}
          onMouseLeave={() => {
            onRowHover?.(null);
          }}
          {...props}
        >
          {props.children}
        </ESTableDraggableRow>
      );
    },
    [
      applyRowStyles,
      dndRowsOptions,
      isLocalDrag,
      onRowHover,
      palette.background.paper,
      rowOptions,
      rows,
      testId,
    ]
  );

  return (
    <ESVirtualizedTable
      instance={instance}
      rows={rows}
      rowHeight={rowHeight}
      scrollerRef={(ref) => {
        const element = ref as HTMLElement;
        dropRef(element);
        tableContainerRef.current = element;
      }}
      renderRow={renderRow}
    />
  );
};
